/**
 * Endereços, nomes de arquivo e listagens do servidor de dados do INPE
 * (dataserver-coids, CORS `*`). Tudo em UTC.
 */
import type { TimeWindow } from './types';

export const INPE_BASE = 'https://dataserver-coids.inpe.br/queimadas/queimadas/focos/csv';
export const TENMIN_DIR_URL = `${INPE_BASE}/10min/`;
export const DAILY_DIR_URL = `${INPE_BASE}/diario/Brasil/`;

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

/**
 * Margem após o fim da janela ao escolher arquivos de 10 min: o arquivo
 * `HHMM` traz detecções de ~20 min antes, às vezes com atraso maior.
 */
export const TENMIN_LATE_MARGIN = 90 * MINUTE;

/**
 * O arquivo `HHMM` traz as detecções do GOES-19 de 20 min antes (medido em
 * 24/09/2026: `focos_10min_…_0900` → focos das 08:40 UTC, `_0810` → 07:50).
 */
export const TENMIN_ATRASO_ARQUIVO = 20 * MINUTE;

/** O INPE mantém ~3 dias de arquivos de 10 min. */
export const TENMIN_RETENTION = 3 * DAY;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** `AAAAMMDD` (UTC). */
export function formatYmd(t: number): string {
  const d = new Date(t);
  return `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}`;
}

/** Meia-noite UTC do dia de `t`. */
export function utcDayStart(t: number): number {
  return Math.floor(t / DAY) * DAY;
}

export function dailyFileName(dayMs: number): string {
  return `focos_diario_br_${formatYmd(dayMs)}.csv`;
}

export function dailyUrl(dayMs: number): string {
  return DAILY_DIR_URL + dailyFileName(dayMs);
}

export function tenMinUrl(name: string): string {
  return TENMIN_DIR_URL + name;
}

const TENMIN_RE = /^focos_10min_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})\.csv$/;

/** Horário (epoch ms, UTC) no nome `focos_10min_AAAAMMDD_HHMM.csv`, ou null. */
export function parseTenMinFileName(name: string): number | null {
  const m = TENMIN_RE.exec(name);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const t = Date.UTC(+y!, +mo! - 1, +d!, +h!, +mi!);
  const c = new Date(t);
  if (c.getUTCDate() !== +d! || +h! > 23 || +mi! > 59) return null;
  return t;
}

export interface TenMinFile {
  name: string;
  /** Horário no nome do arquivo (publicação), epoch ms UTC. */
  t: number;
}

/** Lê a listagem HTML do Apache e devolve os arquivos de 10 min, em ordem. */
export function parseApacheListing(html: string): TenMinFile[] {
  const re = /href="(focos_10min_\d{8}_\d{4}\.csv)"/g;
  const seen = new Set<string>();
  const files: TenMinFile[] = [];
  for (let m = re.exec(html); m; m = re.exec(html)) {
    const name = m[1]!;
    if (seen.has(name)) continue;
    seen.add(name);
    const t = parseTenMinFileName(name);
    if (t !== null) files.push({ name, t });
  }
  files.sort((a, b) => a.t - b.t);
  return files;
}

/**
 * Arquivos de 10 min necessários para a janela: horário no nome em
 * [start, end + margem] (limitado ao que existe na listagem).
 */
export function selectTenMinFiles(
  files: TenMinFile[],
  range: TimeWindow,
  lateMargin = TENMIN_LATE_MARGIN,
): TenMinFile[] {
  const to = range.end + lateMargin;
  return files.filter((f) => f.t >= range.start && f.t <= to);
}

/** Dias UTC (meia-noite, epoch ms) que se sobrepõem à janela. */
export function utcDaysInRange(range: TimeWindow): number[] {
  const days: number[] = [];
  if (!(range.end >= range.start)) return days;
  for (let d = utcDayStart(range.start); d <= range.end; d += DAY) days.push(d);
  return days;
}

/**
 * O arquivo diário do dia D cresce de hora em hora durante D e é fechado por
 * volta das 12:05 UTC de D+1. Até lá precisa ser revalidado a cada atualização.
 */
export function isDailyFileMutable(dayMs: number, now: number): boolean {
  return now < dayMs + DAY + 13 * HOUR;
}
